import { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ConfirmModal from "@/components/ui/confirm-modal";
import type {
  EducationDetailDto,
  EnrollmentDto,
  ObjectiveDto,
} from "../../../types/client-education-detail.types";
import {
  ObjectiveCompletionStatus,
} from "../../../types/client-education-detail.types";
import { clientEducationDetailApi } from "../../../services/client-education-detail.api";
import {
  resolveFollowUpAction,
  type EducationFollowUpAction,
} from "../../../utils/follow-up-url";
import { ChapterAccordion } from "./ChapterAccordion";
import { ObjectiveRow } from "./ObjectiveRow";
import { InlinePlayer } from "./InlinePlayer";
import sectionStyles from "../sections.module.scss";
import styles from "./ContentSection.module.scss";

interface Props {
  education: EducationDetailDto;
  enrollment: EnrollmentDto;
  autoSelectTrigger: number; // bump to auto-select last-in-progress
  onObjectiveUpdated: (updated: ObjectiveDto) => void;
  onRequestRefresh?: () => void | Promise<void>;
}

function flattenObjectives(education: EducationDetailDto): ObjectiveDto[] {
  const out: ObjectiveDto[] = [];
  for (const c of education.chapters ?? []) {
    for (const o of c.objectives ?? []) out.push(o);
  }
  return out;
}

function findResumeTarget(flat: ObjectiveDto[]): ObjectiveDto | null {
  const inProgress = flat.find(
    (o) => (o.completionStatus ?? o.status) === ObjectiveCompletionStatus.IN_PROGRESS
  );
  if (inProgress) return inProgress;
  const notStarted = flat.find(
    (o) => (o.completionStatus ?? o.status) === ObjectiveCompletionStatus.NOT_STARTED
  );
  return notStarted ?? flat[0] ?? null;
}

export function ContentSection(props: Props) {
  const { t } = useTranslation();
  const {
    education,
    enrollment,
    autoSelectTrigger,
    onObjectiveUpdated,
    onRequestRefresh,
  } = props;
  const chapters = education.chapters ?? [];
  const flat = useMemo(() => flattenObjectives(education), [education]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const lastSaveRef = useRef<Record<string, number>>({});
  const lastReportedSecondsRef = useRef<Record<string, number>>({});
  const PROGRESS_POST_INTERVAL_MS = 10_000;

  useEffect(() => {
    setActiveId((prev) => prev ?? findResumeTarget(flat)?.id ?? null);
  }, [flat]);

  useEffect(() => {
    if (autoSelectTrigger > 0) {
      const target = findResumeTarget(flat);
      if (target) setActiveId(target.id);
    }
  }, [autoSelectTrigger, flat]);

  const activeIndex = flat.findIndex((o) => o.id === activeId);
  const activeObjective = activeIndex >= 0 ? flat[activeIndex] : null;

  const navigate = useNavigate();
  const [followUp, setFollowUp] = useState<EducationFollowUpAction | null>(null);

  async function markComplete(
    obj: ObjectiveDto,
    opts?: { watchedSeconds?: number; lastPositionSeconds?: number }
  ) {
    try {
      const followUpAction = await clientEducationDetailApi.completeObjective(
        enrollment.id,
        obj.id,
        opts,
      );
      onObjectiveUpdated({
        ...obj,
        completionStatus: ObjectiveCompletionStatus.COMPLETED,
        status: ObjectiveCompletionStatus.COMPLETED,
      });
      toast.success(i18n.t("contentSection.completedSuccess"));
      if (followUpAction) {
        // Son objective + sınav/anket aşaması varsa: popup göster
        setFollowUp(followUpAction);
      }
    } catch {
      toast.error(i18n.t("contentSection.completedError"));
    }
  }

  function handleFollowUpConfirm() {
    const snapshot = followUp;
    setFollowUp(null);
    const resolved = resolveFollowUpAction(snapshot?.actionUrl);
    if (!resolved) return;
    const mergedState =
      resolved.path === "/dashboard/client-polls"
        ? { ...(resolved.state ?? {}), pollTitle: snapshot?.title }
        : resolved.state;
    navigate(resolved.path, mergedState ? { state: mergedState } : undefined);
  }

  function handleFollowUpClose() {
    setFollowUp(null);
  }

  function saveProgress(
    obj: ObjectiveDto,
    s: { watchedSeconds: number; lastPositionSeconds: number; progressPercent: number },
    opts?: { force?: boolean }
  ) {
    const last = lastSaveRef.current[obj.id] ?? 0;
    const now = Date.now();
    if (!opts?.force && now - last < PROGRESS_POST_INTERVAL_MS) return;
    const prevReported = lastReportedSecondsRef.current[obj.id] ?? 0;
    const rawDelta = s.watchedSeconds - prevReported;
    // Backend her çağrıda delta'yı maxIntervalSeconds (varsayılan 12 sn) ile sınırlar.
    // Force modunda kalan delta 12'den büyük olabilir; clamp edip en azından son saniyeleri
    // bildirerek backend'in endGap toleransıyla otomatik tamamlama yapmasına izin veriyoruz.
    const MAX_DELTA = 12;
    let delta = 0;
    if (opts?.force && rawDelta > 0) {
      delta = Math.min(rawDelta, MAX_DELTA);
    } else if (rawDelta > 0 && rawDelta <= MAX_DELTA) {
      delta = rawDelta;
    }
    lastSaveRef.current[obj.id] = now;
    if (delta > 0) {
      lastReportedSecondsRef.current[obj.id] = prevReported + delta;
    }
    if (delta <= 0) return;
    void clientEducationDetailApi
      .updateObjectiveProgress(enrollment.id, obj.id, {
        ...s,
        watchedSecondsDelta: delta,
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
  }

  const isSingle = chapters.length === 1;
  const headerTotalMin = chapters.reduce(
    (acc, c) => acc + ((c.durationHours ?? 0) * 60 + (c.durationMinutes ?? 0)),
    0
  );
  const doneCount = flat.filter(
    (o) => (o.completionStatus ?? o.status) === ObjectiveCompletionStatus.COMPLETED
  ).length;

  // Eğitim başlamadıysa içerik (video) erişimi kapalı. Backend de aynı
  // kontrolü uyguluyor (EDUCATION_NOT_YET_STARTED).
  const startMs = education.startDate ? new Date(education.startDate).getTime() : null;
  const notStartedYet = startMs != null && !Number.isNaN(startMs) && startMs > Date.now();
  if (notStartedYet) {
    const startLabel = new Date(education.startDate as string).toLocaleDateString(
      "tr-TR",
      { day: "2-digit", month: "long", year: "numeric" },
    );
    return (
      <section id="content" className={sectionStyles.section}>
        <div className={sectionStyles.header}>
          <h2>{t("clientContentSection.title")}</h2>
        </div>
        <div
          style={{
            padding: "24px",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            borderRadius: 10,
            textAlign: "center",
            color: "#475569",
          }}
        >
          <strong>Eğitim {startLabel} tarihinde başlayacak.</strong>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            Başlangıç tarihine kadar video izleme, sınav ve anket kapalıdır.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="content" className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <h2>{t("clientContentSection.title")}</h2>
        <span>
          {doneCount} / {flat.length} tamamlandı · toplam ~{headerTotalMin} dk
        </span>
      </div>

      <div className={styles.featuredPlayerPane}>
        <InlinePlayer
          objective={activeObjective}
          chapter={
            activeObjective
              ? chapters.find((c) =>
                  c.objectives?.some((o) => o.id === activeObjective.id),
                ) ?? null
              : null
          }
          virtualClassrooms={education.virtualClassrooms}
          onMarkComplete={markComplete}
          onProgress={saveProgress}
          onRequestRefresh={onRequestRefresh}
          onPrev={
            activeIndex > 0 ? () => setActiveId(flat[activeIndex - 1].id) : undefined
          }
          onNext={
            activeIndex >= 0 && activeIndex < flat.length - 1
              ? () => setActiveId(flat[activeIndex + 1].id)
              : undefined
          }
        />
      </div>

      {(chapters.length > 1 || flat.length > 1) && (
        <div className={styles.layout}>
          <aside className={styles.nav}>
            {isSingle ? (
              <div className={styles.objList}>
                {flat.map((o) => (
                  <ObjectiveRow
                    key={o.id}
                    objective={o}
                    active={o.id === activeId}
                    onSelect={() => setActiveId(o.id)}
                  />
                ))}
              </div>
            ) : (
              <ChapterAccordion
                chapters={chapters}
                activeObjectiveId={activeId}
                onSelectObjective={(o) => setActiveId(o.id)}
              />
            )}
          </aside>
        </div>
      )}

      <ConfirmModal
        isOpen={!!followUp}
        onClose={handleFollowUpClose}
        onConfirm={handleFollowUpConfirm}
        title={followUp?.title ?? ""}
        message={followUp?.message ?? ""}
        confirmText={followUp?.buttonLabel ?? "Devam Et"}
        cancelText="Daha sonra"
      />
    </section>
  );
}
